import { useState } from 'react';
import { cn } from '@/lib/utils';
import NoItems from '@/components/ui/NoItems.tsx';
import { Card } from '@/components/ui/Card.tsx';
import { columnLabelClass } from '@/components/ui/typography.ts';
import { PlannedTransactionPaymentStatus } from '@/features/public/bookingSummary/types/PlannedTransactionPaymentStatus.ts';
import { InstallmentVm } from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import { sumAmounts } from '@/features/dashboard/bookingChanges/utils/money.ts';
import MoneyAmountList from '@/features/dashboard/bookingChanges/components/primitives/MoneyAmountList.tsx';
import MarkAsPaidModal from '@/features/dashboard/plannedTransactions/components/MarkAsPaidModal.tsx';
import InstallmentRow from './InstallmentRow';
import PaymentTableTotalRow from './PaymentTableTotalRow';
import RecalculateBalanceButton from './RecalculateBalanceButton';
import {
  columnHeaderCellClass,
  installmentsGrid,
  tableCardEmptyClass,
  tableCardHeaderClass,
  installmentsTotalRowClass,
} from './paymentTableLayout';
import {
  pluralizeInstallments,
  pluralizePaidInstallments,
} from '@/features/dashboard/bookingChanges/utils/plurals.ts';

/** Karta „Zaplanowane raty" — jedyne miejsce w systemie z ratami. */
const InstallmentsCard = ({
  installments,
  clientId,
}: {
  installments: InstallmentVm[];
  clientId: string;
}) => {
  const [markAsPaidId, setMarkAsPaidId] = useState<string | null>(null);
  const paidCount = installments.filter(
    (installment) =>
      installment.status === PlannedTransactionPaymentStatus.PAID,
  ).length;
  // Suma per waluta; liczniki nad tabelą liczą pozycje, nie pieniądze — raty
  // w dwóch walutach to wciąż cztery raty, ale dwie sumy. Rata anulowana przez
  // paczkę zostaje w tabeli jako ślad, ale do zapłaty nie wchodzi — inaczej
  // stopka przeczy wartości rezerwacji tuż obok.
  const total = sumAmounts(
    installments.filter(
      (installment) =>
        installment.status !== PlannedTransactionPaymentStatus.CANCELLED,
    ),
  );

  return (
    <Card className="@container overflow-hidden">
      <div className={tableCardHeaderClass}>
        <span className={columnLabelClass}>Zaplanowane raty</span>
        <span className="text-xs font-semibold text-neutral-400">
          {pluralizeInstallments(installments.length)} ·{' '}
          {pluralizePaidInstallments(paidCount)}
        </span>
        <span className="-my-1 ml-auto self-center">
          <RecalculateBalanceButton clientId={clientId} />
        </span>
      </div>

      {installments.length === 0 ? (
        <div className={tableCardEmptyClass}>
          <NoItems
            title="Brak rat"
            description="Ta rezerwacja nie ma jeszcze harmonogramu płatności."
          />
        </div>
      ) : (
        <>
          <div role="table" aria-label="Zaplanowane raty">
            <div
              role="row"
              className={cn(
                installmentsGrid,
                '@max-[600px]:hidden border-b border-neutral-100',
              )}
            >
              <div role="columnheader" className={columnHeaderCellClass}>
                Rata
              </div>
              <div role="columnheader" className={columnHeaderCellClass}>
                Termin
              </div>
              <div
                role="columnheader"
                className={cn(columnHeaderCellClass, 'text-right')}
              >
                Kwota
              </div>
              <div
                role="columnheader"
                className={cn(columnHeaderCellClass, 'text-right')}
              >
                Status
              </div>
              <div role="columnheader" className={columnHeaderCellClass}>
                <span className="sr-only">Akcje</span>
              </div>
            </div>

            {installments.map((installment) => (
              <InstallmentRow
                key={installment.id}
                installment={installment}
                onMarkAsPaid={(row) => setMarkAsPaidId(row.id)}
              />
            ))}
          </div>

          <PaymentTableTotalRow
            className={installmentsTotalRowClass}
            label="Suma rat"
            value={<MoneyAmountList values={total} />}
          />
        </>
      )}

      {markAsPaidId && (
        <MarkAsPaidModal
          plannedTransactionId={markAsPaidId}
          clientId={clientId}
          onClose={() => setMarkAsPaidId(null)}
        />
      )}
    </Card>
  );
};

export default InstallmentsCard;
